// Turns a recognizer's stream of "what I'm hearing right now" hypotheses into
// short chunks that can be translated while the speaker is still talking — the way a
// simultaneous interpreter works: deliver each idea as soon as it is complete, without
// waiting for the whole paragraph, and without cutting an idea in half.
//
// Why this exists: a recognizer only declares a sentence FINAL after it hears a pause. A
// speaker who talks continuously therefore produces one very long final result, and if only
// finals are translated the audience waits for the whole paragraph. This class sits between
// the recognizer and the server:
//
//   interim hypothesis --> [chunker] --> onCommit(chunk)   translate + broadcast now (permanent caption)
//                                   \--> onPartial(tail)   live provisional line (replaced as speech continues)
//   final result       --> [chunker] --> onCommit(rest)    only the part NOT already committed
//
// WHERE a chunk ends decides how good its translation is, so in order of preference a chunk ends:
//   1. at a finished sentence (the recognizer's punctuation), as soon as it is safe;
//   2. at a clause boundary — a comma, or the start of a new clause ("… the first phase | and we
//      are now moving on", "… | because", "… | الذي", "… | mais") — once the clause is long enough;
//   3. when the speaker PAUSES (no new words for a moment): everything said so far is one idea;
//   4. only if none of those happens, when a word/time cap is reached (after a short wait for a
//      natural boundary, then cut without leaving the thought hanging on "and" / "the" / "we").
// Whatever the reason for a cut, it never falls inside a multi-word dictionary phrase handed in with
// setProtectedPhrases() ("mineral | deposits"): the translator and the glossary need the whole term.
//
// It is deliberately free of any browser/React dependency so it can be unit tested. Time-based
// behaviour (step 3) is driven by the owner calling poll() regularly, with an injectable clock.

export interface ChunkerOptions {
  /**
   * Soft word cap: once this many not-yet-committed words are waiting and no natural boundary is in
   * reach, the chunker waits up to `capSlackWords` more words for one, then cuts (so the longest chunk
   * is about maxWords + capSlackWords).
   */
  maxWords?: number;
  /** Words kept back at the end of a chunk: the recognizer may still revise the newest words. */
  holdBackWords?: number;
  /** Also commit when speech has run this long (ms) without a commit... */
  maxMs?: number;
  /** ...provided at least this many words are waiting (avoids translating 1-2 word scraps). */
  minWordsForTimedCommit?: number;
  /** Send provisional updates at most this often (ms). 0 disables throttling. */
  partialIntervalMs?: number;
  /** A chunk is never cut before this many words, however natural the boundary (avoids scraps). */
  minChunkWords?: number;
  /** A clause boundary (comma, "and we…", "because…") ends a chunk once at least this many words precede it. */
  clauseWords?: number;
  /** The speaker paused: no new words for this long (ms) and at least minChunkWords waiting -> commit them. 0 disables. */
  pauseCommitMs?: number;
  /** Extra words to wait for a natural boundary once maxWords is reached (see maxWords). */
  capSlackWords?: number;
  /** A finished chunk, ready to translate and broadcast permanently. */
  onCommit: (text: string) => void;
  /** The current unfinished tail, for the live provisional line. Empty string clears it. */
  onPartial: (text: string) => void;
  now?: () => number;
}

// Tuning: smaller chunks reach listeners sooner but give the translator less
// context; larger chunks translate better but arrive later. The live provisional
// line (onPartial) already gives instant feedback, so chunks favour quality a little.
const DEFAULTS = {
  maxWords: 7,
  holdBackWords: 2,
  maxMs: 3000,
  minWordsForTimedCommit: 5,
  partialIntervalMs: 250,
  minChunkWords: 3,
  clauseWords: 5,
  pauseCommitMs: 400,
  capSlackWords: 3,
};

// Words that should not END a chunk: cutting "…the new project timeline and | budget…"
// after "and" gives the translator a fragment that ends mid-thought. Such words
// are moved to the start of the next chunk instead. English, Arabic and French. Subject
// pronouns are here too: "…in today's meeting we | will discuss…" leaves the verb behind.
const DANGLING = new Set([
  "a", "an", "the", "and", "or", "but", "of", "to", "in", "on", "at", "for", "with", "by", "from", "as", "that",
  "among", "between", "without", "within", "against", "toward", "towards", "into", "onto", "upon", "across",
  "throughout", "beneath", "beside", "alongside",
  // Seen ending real chunks in a recorded test ("...redefined through | lower carbon", "...is not only about |",
  // "...entering a new | era", "...this is where |"): words that always need what follows them.
  "about", "through", "over", "under", "after", "before", "during", "since", "until", "while", "per", "via",
  "where", "what", "who", "whom", "whose", "how", "why", "whether", "than",
  "more", "most", "less", "new", "next", "other", "another", "every", "each", "some", "any", "many", "much", "such", "same", "own",
  "become", "becomes", "became", "been", "being", "has", "have", "had", "do", "does", "did", "may", "might", "must", "should", "shall",
  "which", "is", "are", "was", "were", "be", "will", "would", "can", "could", "our", "your", "their", "its",
  "we", "i", "you", "they", "he", "she", "it", "this", "these", "those", "not", "if", "when", "because",
  "و", "في", "من", "على", "إلى", "الى", "عن", "مع", "أن", "ان", "التي", "الذي", "هذا", "هذه", "لكن", "لأن", "ثم", "حيث",
  "le", "la", "les", "un", "une", "des", "de", "du", "et", "ou", "à", "au", "aux", "en", "pour", "avec", "que", "qui",
  "nous", "je", "il", "elle", "ils", "vous", "on", "ce", "cette", "ces", "mais", "car", "si",
]);

// Words that OPEN a new clause: the natural place to cut is just before them.
const CLAUSE_STARTERS = new Set([
  // English
  "but", "because", "although", "though", "while", "whereas", "however", "therefore", "moreover", "which", "unless",
  "until", "whenever", "otherwise", "when", "if", "where", "whose",
  // Arabic
  "لكن", "لكنّ", "لأن", "لان", "ثم", "حيث", "بينما", "إذ", "إذا", "عندما", "حين", "كما", "لذلك", "بالتالي", "الذي", "التي",
  "الذين", "اللذين", "غير", "أما", "اما",
  // French
  "mais", "car", "donc", "alors", "parce", "puisque", "lorsque", "quand", "pendant", "tandis", "cependant", "toutefois",
  "ainsi", "dont", "où", "si",
]);

// Coordinators ("and", "or", "so", "et", "ou") start a new clause only when a new subject follows:
// "we finished the first phase | and we are now…" (cut) vs "salt and pepper" (don't).
const COORDINATORS = new Set(["and", "or", "so", "et", "ou", "donc"]);
const SUBJECT_WORDS = new Set([
  "we", "i", "you", "they", "he", "she", "it", "this", "that", "these", "those", "there", "the", "our", "their", "my", "your",
  "nous", "je", "il", "elle", "ils", "elles", "vous", "on", "ce", "cette", "ces", "le", "la", "les", "notre", "nos", "leur",
]);

// A full stop after these does not end a sentence.
const ABBREVIATIONS = new Set(["mr", "mrs", "ms", "dr", "prof", "vs", "inc", "ltd", "co", "no", "fig", "approx", "st"]);

const SENTENCE_END = /[.!?؟…]["'”’)\]]*$/;
const CLAUSE_END = /[,;:،؛]["'”’)\]]*$/;

const tokenize = (text: string): string[] => text.trim().split(/\s+/).filter(Boolean);
const normalize = (token: string): string => token.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
/** Like normalize, but "Association's" == "association": a possessive must not hide a glossary phrase. */
const phraseToken = (token: string): string => normalize(token.replace(/['’]s$/i, ""));

/** Longest dictionary phrase (in words) the chunker will try to keep whole. */
const MAX_PROTECTED_WORDS = 8;

/** Is there a natural boundary right after the first `take` words of `waiting`? */
function boundaryAfter(waiting: string[], take: number): "sentence" | "clause" | null {
  const last = waiting[take - 1];
  if (last === undefined) return null;
  if (SENTENCE_END.test(last) && !ABBREVIATIONS.has(normalize(last))) return "sentence";
  if (CLAUSE_END.test(last)) return "clause";
  const next = waiting[take];
  if (next === undefined) return null; // can't tell yet: the next word hasn't been heard
  const nextWord = normalize(next);
  if (CLAUSE_STARTERS.has(nextWord)) return "clause";
  if (COORDINATORS.has(nextWord)) {
    const after = waiting[take + 1];
    if (after !== undefined && SUBJECT_WORDS.has(normalize(after))) return "clause";
  }
  return null;
}

export class UtteranceChunker {
  private readonly opts: Required<Omit<ChunkerOptions, "onCommit" | "onPartial" | "now">>;
  private readonly onCommit: (text: string) => void;
  private readonly onPartial: (text: string) => void;
  private readonly now: () => number;

  /** How many words of the CURRENT recognizer segment have already been committed. */
  private committedCount = 0;
  private lastCommittedWord = "";
  /** When the current run of uncommitted speech began (or the last commit happened). */
  private runStartedAt: number | null = null;
  /** Latest full hypothesis for this segment, kept so flush() can commit a tail that never got a final. */
  private lastTokens: string[] = [];
  /** When the hypothesis last CHANGED (a repeat of identical text does not count): the speaker paused if this is old. */
  private lastChangeAt = 0;

  /** Multi-word dictionary phrases a chunk boundary must not fall inside, indexed by their first word. */
  private phrases = new Map<string, string[][]>();
  private longestPhrase = 0;

  private lastPartialSent: string | null = null;
  private lastPartialAt = 0;
  private pendingPartial: string | null = null;
  private partialTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: ChunkerOptions) {
    const { onCommit, onPartial, now, ...rest } = options;
    this.onCommit = onCommit;
    this.onPartial = onPartial;
    this.now = now ?? Date.now;
    this.opts = { ...DEFAULTS, ...stripUndefined(rest) };
  }

  /** Change chunk size / timing on the fly (e.g. the operator picked another speed); takes effect from the next word. */
  setOptions(options: Partial<Omit<ChunkerOptions, "onCommit" | "onPartial" | "now">>): void {
    Object.assign(this.opts, stripUndefined(options));
  }

  /**
   * Dictionary phrases such as "mineral deposits" or "Arab Fertilizer Association": a chunk is never cut
   * in the middle of one, so the translator sees the whole term and the dictionary can match it.
   * Single words are ignored (nothing to split). Replaces any previous list.
   */
  setProtectedPhrases(phrases: string[]): void {
    const index = new Map<string, string[][]>();
    let longest = 0;
    for (const phrase of phrases) {
      const tokens = tokenize(phrase).map(phraseToken).filter(Boolean);
      if (tokens.length < 2 || tokens.length > MAX_PROTECTED_WORDS) continue;
      const bucket = index.get(tokens[0]);
      if (bucket) bucket.push(tokens);
      else index.set(tokens[0], [tokens]);
      longest = Math.max(longest, tokens.length);
    }
    this.phrases = index;
    this.longestPhrase = longest;
  }

  /** The recognizer's current full hypothesis for the segment being spoken. */
  feedInterim(text: string): void {
    const tokens = tokenize(text);
    if (tokens.length === 0) return;
    if (tokens.join(" ") !== this.lastTokens.join(" ")) this.lastChangeAt = this.now();
    this.lastTokens = tokens;
    if (this.runStartedAt === null) this.runStartedAt = this.now();

    // The recognizer revised an earlier hypothesis and it is now shorter than what
    // we already sent. Nothing new to say; wait for it to catch up.
    if (tokens.length <= this.committedCount) {
      this.emitPartial("");
      return;
    }

    // One update can complete more than one chunk (e.g. two short sentences arrived together).
    for (;;) {
      const waiting = tokens.slice(this.committedCount);
      const take = this.nextCut(waiting, waiting.length - this.opts.holdBackWords);
      if (take <= 0) break;
      this.commitWords(waiting.slice(0, take));
      this.committedCount += take;
      this.runStartedAt = this.now();
    }
    this.emitPartial(tokens.slice(this.committedCount).join(" "));
  }

  /**
   * Call regularly (e.g. every 100 ms) while the microphone is live. If the speaker has paused —
   * no new words for `pauseCommitMs` — the words waiting are one finished idea: send them now,
   * instead of waiting for the recognizer to close the sentence (which takes longer still).
   */
  poll(): void {
    const { pauseCommitMs, minChunkWords, holdBackWords } = this.opts;
    if (pauseCommitMs <= 0 || this.lastTokens.length === 0) return;
    const waiting = this.lastTokens.slice(this.committedCount);
    if (waiting.length < minChunkWords) return;
    if (this.now() - this.lastChangeAt < pauseCommitMs) return;

    // A quiet gap in the recognizer's INTERIM stream is not proof the speaker paused — it is often
    // just the recognizer thinking right before it emits `final`. Committing every waiting word here
    // would race that `final`: if it wins, feedFinal() is left to deliver whatever tiny remainder is
    // left with NO minimum-size protection (it must never drop a word, however few are left), handing
    // the translator a context-free scrap — proven to produce a wrong translation, not just a rough
    // one (see backend/scripts/_diag/REPORT.txt: an isolated trailing word translated as the wrong
    // sense of an otherwise-correct sentence). Hold back the same buffer the size-cap path already
    // uses, so a real `final` arriving moments later still has a normal-sized remainder to deliver.
    let take = Math.max(0, waiting.length - holdBackWords);
    while (take > 2 && DANGLING.has(normalize(waiting[take - 1]))) take--;
    take = this.settleCut(waiting, take);
    if (take < minChunkWords) return; // not enough settled yet — wait for more words or for feedFinal

    this.commitWords(waiting.slice(0, take));
    this.committedCount += take;
    this.runStartedAt = null;
    this.emitPartial(waiting.slice(take).join(" "));
  }

  /** The recognizer finished a segment (it heard a pause). Commits whatever wasn't committed yet. */
  feedFinal(text: string): void {
    const tokens = tokenize(text);
    const cut = this.findCut(tokens);
    const rest = tokens.slice(cut);
    this.resetSegment();
    if (rest.length > 0) this.onCommit(rest.join(" "));
    this.emitPartial("", true);
  }

  /** Commit any uncommitted tail (recognizer stopped without a final) and clear the live line. */
  flush(): void {
    const rest = this.lastTokens.slice(this.committedCount);
    this.resetSegment();
    if (rest.length > 0) this.onCommit(rest.join(" "));
    this.emitPartial("", true);
  }

  /** Drop all state without committing anything. */
  reset(): void {
    this.resetSegment();
    this.emitPartial("", true);
  }

  /**
   * How many of the waiting words to hand over now (0 = keep listening). `limit` is how many words
   * are old enough that the recognizer is unlikely to revise them.
   */
  private nextCut(waiting: string[], limit: number): number {
    const take = this.rawCut(waiting, limit);
    return take > 0 ? this.settleCut(waiting, take) : 0;
  }

  /**
   * Moves a cut that would split a dictionary phrase to where the phrase starts (or, if that would leave a
   * scrap, to just after it). Returns 0 to wait for more speech when the phrase may still be unfolding.
   */
  private keepPhrasesWhole(waiting: string[], take: number): number {
    if (this.longestPhrase < 2 || take <= 0 || take >= waiting.length) return take;
    // Safety valve: never hold a chunk back for a phrase forever.
    if (waiting.length >= this.opts.maxWords + this.opts.capSlackWords + this.longestPhrase) return take;

    const tokens = waiting.map(phraseToken);
    let coverStart = -1;
    let coverEnd = -1; // end (exclusive) of the covering phrase if it is fully heard, else -1
    for (let start = Math.max(0, take - this.longestPhrase + 1); start < take; start++) {
      for (const phrase of this.phrases.get(tokens[start]) ?? []) {
        const known = Math.min(phrase.length, tokens.length - start);
        let i = 0;
        while (i < known && tokens[start + i] === phrase[i]) i++;
        if (i < known) continue; // the words here are not this phrase
        const complete = known === phrase.length;
        if (complete && start + phrase.length <= take) continue; // the phrase lies wholly before the cut
        if (coverStart === -1 || start < coverStart) {
          coverStart = start;
          coverEnd = complete ? start + phrase.length : -1;
        }
      }
    }
    if (coverStart === -1) return take;
    if (coverStart >= this.opts.minChunkWords) return coverStart; // cut just before the phrase
    return coverEnd !== -1 ? coverEnd : 0; // too short a chunk: take the phrase along, or wait to hear all of it
  }

  /** Applies phrase protection, then re-checks the "never end on a dangling word" rule for any cut it moved. */
  private settleCut(waiting: string[], take: number): number {
    let cut = take;
    for (let pass = 0; pass < 6; pass++) {
      const kept = this.keepPhrasesWhole(waiting, cut);
      if (kept === cut) return cut;
      if (kept <= 0) return 0;
      const movedBack = kept < cut;
      cut = kept;
      // Moved to just AFTER a phrase: its last word ends the chunk, even if that word is a "dangling" one
      // ("thank you very much") — trimming it back would split the phrase again. Moved to just BEFORE a
      // phrase: the word now ending the chunk may be a dangling one ("... of the | mineral deposits").
      if (movedBack) while (cut > 2 && DANGLING.has(normalize(waiting[cut - 1]))) cut--;
    }
    return cut;
  }

  private rawCut(waiting: string[], limit: number): number {
    const { minChunkWords, clauseWords, maxWords, maxMs, minWordsForTimedCommit } = this.opts;

    // 1. A finished sentence.
    for (let take = minChunkWords; take <= limit; take++) {
      if (boundaryAfter(waiting, take) === "sentence") return take;
    }
    // 2. A natural clause boundary, once the clause is long enough to be worth sending.
    for (let take = Math.max(clauseWords, minChunkWords); take <= limit; take++) {
      if (boundaryAfter(waiting, take) === "clause") return take;
    }
    // 3. No natural boundary: a size or time cap forces a cut.
    const tooMany = waiting.length >= maxWords;
    const tooLong =
      waiting.length >= minWordsForTimedCommit && this.runStartedAt !== null && this.now() - this.runStartedAt >= maxMs;
    if (!tooMany && !tooLong) return 0;

    // Prefer the last boundary in the safe zone (but not so early that the chunk is a scrap)...
    const earliest = Math.max(minChunkWords, Math.floor(limit / 2));
    for (let take = limit; take >= earliest; take--) {
      if (boundaryAfter(waiting, take)) return take;
    }
    // ...otherwise a word cap is worth a short wait: an idea usually finishes within a word or two, and
    // "...the second | phase" is a worse cut than a slightly later one. (Not for a time cap: the speaker is slow.)
    if (tooMany && !tooLong && waiting.length < maxWords + this.opts.capSlackWords) return 0;

    // Out of patience: cut at the safe limit, without leaving the chunk hanging on "and", "the", "we"...
    let take = Math.max(1, limit);
    while (take > 2 && DANGLING.has(normalize(waiting[take - 1]))) take--;
    return take;
  }

  private commitWords(words: string[]): void {
    this.lastCommittedWord = normalize(words[words.length - 1]);
    this.onCommit(words.join(" "));
  }

  private resetSegment(): void {
    this.committedCount = 0;
    this.lastCommittedWord = "";
    this.runStartedAt = null;
    this.lastTokens = [];
  }

  /**
   * Where the final text picks up after the words we already committed. Normally
   * that is simply `committedCount`, but the final result is re-formatted
   * ("twenty five" -> "25", punctuation, capitals), which can shift word counts by a
   * word or two — so look for the last committed word near that position.
   */
  private findCut(finalTokens: string[]): number {
    if (this.committedCount === 0) return 0;
    const fallback = Math.min(this.committedCount, finalTokens.length);
    if (!this.lastCommittedWord) return fallback;
    for (let delta = 0; delta <= 3; delta++) {
      for (const j of delta === 0 ? [this.committedCount] : [this.committedCount - delta, this.committedCount + delta]) {
        if (j >= 1 && j <= finalTokens.length && normalize(finalTokens[j - 1]) === this.lastCommittedWord) return j;
      }
    }
    return fallback;
  }

  private emitPartial(text: string, force = false): void {
    const interval = this.opts.partialIntervalMs;
    if (this.partialTimer) {
      clearTimeout(this.partialTimer);
      this.partialTimer = null;
    }
    if (text === this.lastPartialSent && this.pendingPartial === null) return;

    const sinceLast = this.now() - this.lastPartialAt;
    if (force || text === "" || interval <= 0 || sinceLast >= interval) {
      this.sendPartial(text);
      return;
    }
    // Too soon after the last update: send the newest text when the interval ends,
    // so the final words before a pause are never left out.
    this.pendingPartial = text;
    this.partialTimer = setTimeout(() => {
      this.partialTimer = null;
      const latest = this.pendingPartial;
      if (latest !== null) this.sendPartial(latest);
    }, interval - sinceLast);
  }

  private sendPartial(text: string): void {
    this.pendingPartial = null;
    this.lastPartialSent = text;
    this.lastPartialAt = this.now();
    this.onPartial(text);
  }
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
}
